<!-- src/pages/MyPage.vue -->
<script setup>
import { Transition } from 'vue';
import { useRoute } from 'vue-router';
import { cloneDeep } from 'lodash';

import { DEV_MODE } from '@/config/dev-mode';
import { useMyInfoStore } from '@/store/modules/myinfo';
import { useManseStore } from '@/store/modules/manseStore';
import { usePrimarySajuStore } from '@/store/modules/primarySaju';
import { contentOptionMap } from '@/components/saju/data/manseCardLogData';

import { getMemoList } from '@/api/UserGroup';
import { addContentLogAPI } from '@/api/logServiceApi';
import { fastSearch, fastSearchByMySaju, userGroupDel } from '@/api/Eoway';
import { getDetail, updateGuestUserName } from '@/api/UserGroup';

// composables
import fiveMixin from '@/composables/fiveMixin';
import useFunctions from '@/composables/useFunctions';
import {
  groupListOpen,
  isPopSajuGroupChange,
  closeGroupChange,
  openGroupSetting,
} from '@/composables/useGroup';
import { getUserLogin } from '@/composables/useAuthStore';
import { usePopups } from '@/composables/saju/usePopups';
import { useClientType } from '@/composables/useClientType';

// components
import LoadingSpinner from '@/components/saju/LoadingSpinner.vue';
import SajuResultHeader from '@/components/saju/SajuResultHeader.vue';
import SajuResultPro from '@/components/saju/SajuResultPro.vue';
import SajuMyeongsik from '@/components/saju/SajuMyeongsik.vue';
import OhaengChart from '@/components/saju/OhaengChart.vue';
import OhaengSummary from '@/components/saju/OhaengSummary.vue';
import WoonContainer from '@/components/saju/WoonContainer.vue';
import SinsalCardGrid from '@/components/saju/SinsalCardGrid.vue';

//popup
import PopSajuGroupChange from '@/components/saju-service/PopSajuGroupChange.vue';
import ComLatest from '@/components/saju/ComLatest.vue';
import PopMansePrint from '@/components/saju/PopMansePrint.vue';
import PopNameComponent from '@/components/saju/PopName.vue';
import PopCardDetail from '@/components/saju/PopCardDetail.vue';
import PopIlgan from '@/components/saju/PopIlgan.vue';
import PopAddGroup from '@/components/saju-service/PopAddGroup.vue';
import PopMemo from '@/components/saju-service/PopMemo.vue';
import PopFiveStar from '@/components/saju/PopFiveStar.vue';
import QuickMenu from '@/components/saju/QuickMenu.vue';
import MobileQuickMenu from '@/components/saju/MobileQuickMenu.vue';

//Alert
import AlertComponent from '@/components/common/Alert.vue';
import ConfirmComponent from '@/components/common/Confirm.vue';

// Icons
import IconHeaderBack from '@/components/svg/IconHeaderBack.vue';
import MobileHeader from '@/components/common/MobileHeader.vue';
import PopShareBox from '@/components/saju/PopShareBox.vue';

definePage({ meta: { layout: 'manse' } });

const systemId = import.meta.env.VITE_APP_SYSTEMID;

// 모바일 감지
const isMobile = ref(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768;
};
onMounted(() => {
  checkMobile(); // 마운트 시 한 번 더 체크
  window.addEventListener('resize', checkMobile);
});
onUnmounted(() => {
  window.removeEventListener('resize', checkMobile);
});

// 모바일 퀵메뉴 상태 (탭 전환용)
const mobileActiveTab = ref('daeun');
const handleMobileMenuClick = (menuId) => {
  switch (menuId) {
    case 'ilgan':
      clickIlganButton(toggleProMode.value === 'pro' ? '프로' : '일반');
      break;
    case 'sajupuri':
      validateAnalysisAccess('reading');
      break;
    case 'nangmaniljin':
      validateAnalysisAccess('iljin');
      break;
    case 'nangmansunghyang':
      validateAnalysisAccess('disposition');
      break;
    case 'yongshin':
      validateAnalysisAccess('analysis');
      break;
    case 'daeun':
    case 'ohaengdo':
    case 'sinsal':
      // 탭 전환은 v-model로 처리됨
      break;
  }
};

/** 상세분석 데이터 접근 불가 사주 검사 (시간모름, 미래사주) */
const validateAnalysisAccess = (type) => {
  if (isFutureSaju.value) {
    showDialogAlert('내년 이후 사주는\n상세분석 서비스 제공이 어렵습니다.');
    return;
  }
  if (isUnknownTime.value) {
    showDialogAlert('시간 모름인 사주는\n상세분석 서비스 제공이 어렵습니다.');
    return;
  }
  switch (type) {
    case 'reading':
      popQuickMenuRef.value?.navigateToAnalysis('reading');
      break;
    case 'iljin':
      popQuickMenuRef.value?.openSajuIljinDialog();
      break;
    case 'disposition':
      popQuickMenuRef.value?.navigateToAnalysis('disposition');
      break;
    case 'analysis':
      popQuickMenuRef.value?.navigateToAnalysis('analysis');
      break;
  }
};

const route = useRoute();
const router = useRouter();
const { isApp } = useClientType();

const { ...five } = fiveMixin();
const { ...useFun } = useFunctions();

// 팝업 관련 변수, 함수
const { isPopManseDetail, closePopQuickMenu, closePopManseDetail } = usePopups();

// 대운/세운/월운 관리 컴포저블
import { useWoonManagement } from '@/composables/saju/useWoonManagement.js';
const woonManagement = useWoonManagement();

// 사주 저장 관리 컴포저블
import { useSajuSave } from '@/composables/saju/useSajuSave.js';
const sajuSave = useSajuSave();

const isLoading = ref(false);

const { selectedOptions, setUserSelectOption } = useMyInfoStore();
const manseStore = useManseStore();
const primarySajuStore = usePrimarySajuStore();

const colorMode = ref('fntcolor01');

const rsDataNor = ref(false);

const rsData01 = ref(false);

const formData = ref({
  year: '',
  month: '',
  day: '',
  hour: '',
  min: '',
  gender: null,
  userName: '',
  isLunar: 'false',
  lunar: null,
  isLeapYear: null,
  bornAreaId: null,
  birth_time: null,
  birth: null,
  birthTime: null,
  unknownTime: null,
  option1: true,
  option2: false,
  option3: false,
  option4: true,
  addressTxt: null,
  lang: 'KOR',
  langYN: 'N',
  userGroupId: null,
  shortly: false,
  blCheck: 'N',
});

/** '조회하기'로 받은 사주 데이터 */
const sajuData = ref({});
/** '조회하기'에 입력했던 formData 복사 */
const sajuDataForm = ref({});

const rsDataAll = ref({});
const langMode = ref('CHNKOR'); // 'CHNKOR' | 'CHN' | 'KOR'
const sortMode = ref('desc');

const transdata = ref({});
const inputdata = ref({});
const birthData = ref({});
// 백엔드에서 제공하는 정확한 양력/음력 날짜 (transdata.solar/lunar 대체)
const solarDate = ref(null);
const solarWeekday = ref(null);
const lunarDate = ref(null);
const birthJijanggan = ref({});
const birthTitle = ref('시일월년');
const gongDataVal = ref(null);
const birthFiveCnt = ref({});

const hapDataValEarth = ref(null);
const hapDataVal = ref(null);

const isSaved = ref(false);
const isShowBanner = ref(true);

const birthEightTxt = ref('');
const sajuCreatedDate = ref(null);

// 대운/세운/월운 관련 변수들은 useWoonManagement에서 가져옴
const {
  curDaeun,
  daeunsData,
  yearsData,
  monthsData,
  isDaeunCheck,
  isDaeunYear,
  isDaeunMonth,
  daeunCurNum,
  daeunYearCurNum,
  daeunMonthCurNum,
  hapBlDataSky,
  hapBlDataEarth,
  isSeWoonOpen,
  isWolWoonOpen,
  // 메서드
  getWoon,
  clickDaeunCheck,
  applyWoon,
  reverseDaeunData,
  toggleSeWoon,
  toggleWolWoon,
} = woonManagement;

// 모바일 접기 버튼: 대운/세운 → 세운+월운 토글, 월운 → 월운만 토글
const isMobileSeWoonOpen = ref(true);
const isMobileWolWoonOpen = ref(true);
const isMobileAllSubOpen = computed(() => isMobileSeWoonOpen.value && isMobileWolWoonOpen.value);
const toggleMobileDaeun = () => {
  if (isMobileAllSubOpen.value) {
    isMobileSeWoonOpen.value = false;
    isMobileWolWoonOpen.value = false;
  } else {
    isMobileSeWoonOpen.value = true;
    isMobileWolWoonOpen.value = true;
  }
};
const toggleMobileSeWoon = () => {
  isMobileSeWoonOpen.value = !isMobileSeWoonOpen.value;
  if (!isMobileSeWoonOpen.value) isMobileWolWoonOpen.value = false;
  else isMobileWolWoonOpen.value = true;
};
const toggleMobileWolWoon = () => {
  isMobileWolWoonOpen.value = !isMobileWolWoonOpen.value;
};

// 모바일 세운/월운 컨테이너 ref
const mobileSeunContainerRef = ref(null);
const mobileWolunContainerRef = ref(null);

// 모바일 운 컨테이너에서 선택된(active) 항목이 보이도록 스크롤
const scrollWoonToActive = (containerRef) => {
  setTimeout(() => {
    const container = containerRef.value?.$el;
    if (container && container.classList.contains('data-0102-box')) {
      const activeItem = container.querySelector('.birth-item-box.active');
      if (activeItem) {
        const containerRect = container.getBoundingClientRect();
        const activeRect = activeItem.getBoundingClientRect();
        const scrollOffset = activeRect.left - containerRect.left - (containerRect.width / 2) + (activeRect.width / 2);
        container.scrollLeft += scrollOffset;
      } else {
        container.scrollLeft = container.scrollWidth;
      }
    }
  }, 150);
};

// 모바일에서 세운/월운 데이터 로드 시 오른쪽 끝으로 스크롤
watch(yearsData, (newYearsData) => {
  if (isMobile.value && newYearsData?.sky?.length) {
    scrollWoonToActive(mobileSeunContainerRef);
  }
}, { flush: 'post' });

watch(monthsData, (newMonthsData) => {
  if (isMobile.value && newMonthsData?.sky?.length) {
    scrollWoonToActive(mobileWolunContainerRef);
  }
}, { flush: 'post' });

// 세운/월운 펼침 시 스크롤
watch(isMobileSeWoonOpen, (isOpen) => {
  if (isMobile.value && isOpen) {
    scrollWoonToActive(mobileSeunContainerRef);
  }
});

watch(isMobileWolWoonOpen, (isOpen) => {
  if (isMobile.value && isOpen) {
    scrollWoonToActive(mobileWolunContainerRef);
  }
});

// useWoonManagement 초기화 (의존성 주입은 searchEoway 후에 수행)
/** 대운적용 기능 사용여부*/
const isapplyWoon = ref(true);
// const isapplyWoon = ref(DEV_MODE);

/** 현재세운 데이터 (모바일에서 사용) */
const curSeun = computed(() => {
  if (!yearsData.value?.sky?.length) return { sky: [], earth: [], year: null };
  // 현재 선택된 세운 번호(연도)에 해당하는 데이터 찾기
  const skyData = yearsData.value.sky.find((item) => item.num === daeunYearCurNum.value);
  const earthData = yearsData.value.earth.find((item) => item.num === daeunYearCurNum.value);
  return {
    sky: skyData ? [skyData] : [],
    earth: earthData ? [earthData] : [],
    year: daeunYearCurNum.value, // 현재 선택된 연도
  };
});

/** 첫 대운 변기일 정보 */
const bigluckinfo = computed(() => {
  return sajuData.value?.bigluckinfo || null;
});

/** 첫 대운 변기일 포맷팅 (YYYY-MM-DD 형식으로) */
const firstDaeunDateFormatted = computed(() => {
  if (!bigluckinfo.value?.first_daeun_date) return null;
  // "1912-03-21 11:42:15" -> "1912-03-21"
  return bigluckinfo.value.first_daeun_date.split(' ')[0];
});

/** 사주조회 Params 저장 */
const searchParams = ref(null);

const selectedGroup = ref({});
const selectedListItem = ref({});
const userGroupId = ref(null);
const userGroupName = ref('등록 사주');
const isUnknownTime = ref(false);
const isFutureSaju = ref(false);

const currentCardIndex = ref(null);

//TODO: 전문가용
const toggleProMode = ref('nor'); // 프로: 'pro', 일반: 'nor'
const dataOrder = ref(['hour', 'day', 'month', 'year']);
const firstSegment = ref({});
const secondSegment = ref({});
const thirdSegment = ref({});
const fourthSegment = ref({});

const changeProMode = async (mode) => {
  addContentLogAPI({
    contentType: 'sl_option_pro_gen_opt3',
    option3: mode === 'pro' ? '프로' : '일반',
  });
  toggleProMode.value = mode;
  if (mode === 'pro') await searchEowayTrans('CHNKOR');
  sessionStorage.setItem('toggleProMode', toggleProMode.value);
  // 모드 바뀔 때 일간 팝업 닫기
  if (mode === 'nor') await resetSkyDay();
  isPopIlgan.value = false;
};

const isNormalModeFirst = ref(true);

watch(toggleProMode, (newMode) => {
  if (newMode === 'nor' && isNormalModeFirst.value) {
    isNormalModeFirst.value = false;
    addContentLogAPI({
      contentType: 'sl_option_sort_opt3',
      option3: '시일월년',
    });
    addContentLogAPI({
      contentType: 'sl_option_chn_kor_opt3',
      option3: '전체',
    });
    addContentLogAPI({
      contentType: 'sl_option_color_opt3',
      option3: '바탕색',
    });
  }
});

/** 한글과 한자 혼용 */
// 한자/한글 버전일 경우 프린트는 일부 한자 일부 한글
const showHanja = (value, position) => {
  return langMode.value !== 'KOR' ? five.getHanjaTxt(value, position) : value;
};

async function setData() {
  if (sortMode.value === 'asc') {
    // dataOrder.value = [dataOrder.value[0], ...dataOrder.value.slice(1).reverse()]
    // dataOrderTit.value = [dataOrderTit.value[0], ...dataOrderTit.value.slice(1).reverse()]
  }

  if (rsDataAll.value) {
    const totalLength = rsDataAll.value.hundredsyears.reduce(
      (acc, item) => acc + (item.value ? item.value.length : 0),
      0,
    );

    const segmentLength = 25;
    firstSegment.value = rsDataAll.value?.hundredsyears.reduce((acc, item) => {
      const filteredValue = item.value.filter((v) => v.age <= segmentLength);
      if (filteredValue.length > 0) {
        acc.push({ ...item, value: filteredValue });
      }
      return acc;
    }, []);
    secondSegment.value = rsDataAll.value?.hundredsyears.reduce((acc, item) => {
      const filteredValue = item.value.filter(
        (v) => v.age > segmentLength && v.age <= segmentLength * 2,
      );
      if (filteredValue.length > 0) {
        acc.push({ ...item, value: filteredValue });
      }
      return acc;
    }, []);

    thirdSegment.value = rsDataAll.value?.hundredsyears.reduce((acc, item) => {
      const filteredValue = item.value.filter(
        (v) => v.age > segmentLength * 2 && v.age <= segmentLength * 3,
      );
      if (filteredValue.length > 0) {
        acc.push({ ...item, value: filteredValue });
      }
      return acc;
    }, []);

    fourthSegment.value = rsDataAll.value?.hundredsyears.reduce((acc, item) => {
      //const filteredValue = item.value.filter((v) => v.age > segmentLength * 3)
      const filteredValue = item.value.filter((v) => v.age > segmentLength * 3 && v.age <= 100);
      if (filteredValue.length > 0) {
        acc.push({ ...item, value: filteredValue });
      }
      return acc;
    }, []);
  }
}

const goList = (type) => {
  addContentLogAPI('');
  const currentQuery = router.currentRoute.value.query;
  router.push({
    path: '/saju-service',
    query: currentQuery, // 기존 쿼리 파라미터를 그대로 사용
  });
};

watch(
  () => [route.params],
  async (newParams) => {
    // 콜백 함수를 async로 정의
    const [params] = newParams;
    if (typeof params.id === 'undefined') {
      await router.push(isApp.value ? '/saju-service' : '/blank?path=/manse');
    } else {
      id.value = params?.id;
      pgmode.value = params?.pgmode;

      if (params.pgmode) {
        try {
          await getParams();
          await searchEoway();
        } catch (error) {
          alert('getParams : ' + error.response?.data?.message);
        }
      }
    }
  },
);

const searchEowayDo = async (callback = () => {}) => {
  resetSajuData();
  isSaved.value = false;
  const prevRsData01 = rsData01.value;
  rsData01.value = false;
  isLoading.value = true;
  // isShowBanner.value = false
  formData.value.langYN = 'N';

  try {
    await searchEoway();
  } catch (error) {
    // 에러 발생 시 이전 결과 복원
    rsData01.value = prevRsData01;
    throw error;
  }

  resetSkyDay();
  await nextTick();
  callback();

  // proBodyRef 상태를 watchEffect로 감지
  watchEffect(() => {
    if (proBodyRef.value && proBodyRef.value.resetAll) {
      proBodyRef.value.resetAll();
    } else {
      DEV_MODE && console.log('proBodyRef 초기화 대기 중...');
    }
  });

  await nextTick();

  if (Object.keys(sajuData.value).length !== 0) {
    addContentLogAPI('M_saju_check');
    addContentLogAPI({
      contentType: 'M_saju_check_option_opt3',
      option3: formData.value.option1 ? '야자시' : '일반시',
    });
    addContentLogAPI({
      contentType: 'M_saju_check_option_opt3',
      option3: formData.value.option2 === null ? '모름' : formData.value.option2 ? '동지' : '입춘',
    });
    addContentLogAPI({
      contentType: 'M_saju_check_option_opt3',
      option3: formData.value.option4 ? '한국시' : '표준시',
    });
    addContentLogAPI({
      contentType: 'sl_option_pro_gen_opt3',
      option3: '프로',
    });
  }
};

const oldFormData = ref({});
const rsGuestUserId = ref(null);
const searchEoway = async (useInputData = false, showLoading = true) => {
  if (areaTxt.value === null) {
    formData.value.addressTxt = areaTxt.value;
    formData.value.bornAreaId = null;
  }

  // 내 사주(마스킹된 birth)인 경우 폼 검증을 건너뛴다. fastSearchByMySaju가
  // guestUserId 기반으로 동작하므로 formData.year/month/day가 비어 있어도 무방.
  const isMineForValidation =
    isMySajuFromDetail.value ||
    (!!id.value && primarySajuStore.isMySaju(Number(id.value)));

  if (!useInputData && !isMineForValidation) {
    let formValidate = true;
    if (formData.value.gender === null) {
      showDialogAlert('성별을 확인해 주세요.');
      //alert('성별을 선택해 주세요.');
      formValidate = false;
      return;
    }
    if (
      formData.value.isLunar === null &&
      (formData.value.isLeapYear === null || formData.value.isLeapYear === false)
    ) {
      showDialogAlert('양력, 음력, 윤달을 선택해 주세요.');
      formValidate = false;
      return;
    }
    if (formData.value.year === '' || formData.value.month === '' || formData.value.day === '') {
      showDialogAlert('생년월일을 입력해주세요.');
      formValidate = false;
      return;
    }

    const year = parseInt(formData.value.year, 10);
    if (year < 1900 || year > 2100) {
      showDialogAlert('사주조회는 1900년~2100년까지 가능합니다.');
      formValidate = false;
      return;
    }
    if (
      (formData.value.hour === '' || formData.value.min === '') &&
      formData.value.birth_time !== true
    ) {
      showDialogAlert('출생시간을 입력해주세요.');
      formValidate = false;
      return;
    }

    if (!formValidate) return;

    formData.value.langYN = 'N';
  }

  try {
    if (showLoading !== false) {
      isLoading.value = true;
    }
    const { month, day, hour, min } = formData.value;
    formData.value.birth = `${formData.value.year}-${month.toString().padStart(2, '0')}-${day
      .toString()
      .padStart(2, '0')}`;
    // 시간모름 일때
    if (formData.value.birth_time) {
      formData.value.hour = '--';
      formData.value.min = '--';
      formData.value.birthTime = '25:00';
      formData.value.unknownTime = 'Y';
    }
    if (formData.value.birthTime === '25:00') formData.value.unknownTime = 'Y';
    if (!formData.value.unknownTime) formData.value.unknownTime = 'N';
    if (!formData.value.isLeapYear) formData.value.isLeapYear = false;

    //지역값 없을 시 삭제하고 보내기
    if (formData.value.bornAreaId === null) {
      delete formData.value.bornAreaId;
      delete oldFormData.value.bornAreaId;
    }

    let params = reactive({}); // reactive로 초기화
    if (formData.value.langYN === 'Y') {
      Object.assign(params, oldFormData.value); // oldFormData의 값을 params에 할당
    } else {
      Object.assign(params, formData.value); // formData의 값을 params에 할당
    }

    if (useInputData && sajuDataForm.value) {
      params = sajuDataForm.value;
      params.lang = formData.value.lang;
    }

    const isMine =
      isMySajuFromDetail.value ||
      (!!id.value && primarySajuStore.isMySaju(Number(id.value)));

    // 대운적용을 위한 사주조회 params 저장 (내 사주는 마스킹 대비해 최소 파라미터만 저장)
    searchParams.value = isMine
      ? {
          guestUserId: Number(id.value),
          userName: params.userName,
          lang: params.lang || 'KOR',
        }
      : params;

    const data = isMine
      ? await fastSearchByMySaju(Number(id.value), params.userName, params.lang || 'KOR')
      : await fastSearch(params); // 저장목록 조회

    if (!useInputData) {
      sajuDataForm.value = params;
    }

    isShowBanner.value = false;

    if (data.errmsg?.code === 1) {
      showDialogAlert(data.errmsg.msg);
      return;
    }
    sajuData.value = data;
    rsDataAll.value = data;
    transdata.value = data.transdata;
    inputdata.value = data.inputdata;
    birthData.value = data.birthganji;
    solarDate.value = data.solar;
    solarWeekday.value = data.solarWeekday;
    lunarDate.value = data.lunar;
    birthJijanggan.value = data.jijanggan;
    rsData01.value = true;

    // useWoonManagement 컴포저블 초기화 (의존성 주입)
    woonManagement.initialize({
      inputdata,
      birthData,
      langMode,
      formData,
      searchParams,
      five,
    });

    // 대운 데이터 설정 (컴포저블이 처리)
    woonManagement.setInitialData(data);

    // useSajuSave 컴포저블 초기화
    sajuSave.initialize({
      systemId,
      sajuData,
      transdata,
      solarDate,
      lunarDate,
      userGroupId,
      userGroupName,
      memoContent,
      pgmode,
      id,
      isPopSajuGroupChange,
      showDialogAlert,
      getParams,
    });

    const currentYear = new Date().getFullYear();
    isFutureSaju.value = inputdata.value.year > currentYear;
    isUnknownTime.value = inputdata.value.unknown_time === 'Y';
    const skyValues = [
      five.getHanjaTxt(birthData.value.sky.year.value, 'sky'),
      five.getHanjaTxt(birthData.value.sky.month.value, 'sky'),
      five.getHanjaTxt(birthData.value.sky.day.value, 'sky'),
      birthData.value.sky.hour?.value !== '-'
        ? five.getHanjaTxt(birthData.value.sky.hour.value, 'sky')
        : '-',
    ];
    const earthValues = [
      five.getHanjaTxt(birthData.value.earth.year.value, 'earth'),
      five.getHanjaTxt(birthData.value.earth.month.value, 'earth'),
      five.getHanjaTxt(birthData.value.earth.day.value, 'earth'),
      birthData.value.earth.hour?.value !== '-'
        ? five.getHanjaTxt(birthData.value.earth.hour.value, 'earth')
        : '-',
    ];
    birthEightTxt.value = skyValues.join('') + earthValues.join('');
    if (birthEightTxt.value === '') {
      const skyValues = [
        birthData.value.sky.year.value,
        birthData.value.sky.month.value,
        birthData.value.sky.day.value,
        birthData.value.sky.hour?.value ?? '-',
      ];
      const earthValues = [
        birthData.value.earth.year.value,
        birthData.value.earth.month.value,
        birthData.value.earth.day.value,
        birthData.value.earth.hour?.value ?? '-',
      ];
      birthEightTxt.value = skyValues.join('') + earthValues.join('');
    }

    hapDataVal.value = five
      .convertHap({ hap: data?.hap, isSky: true, langMode: langMode.value })
      .join('ㆍ');
    hapDataValEarth.value = five
      .convertHap({ hap: data?.hap, isSky: false, langMode: langMode.value })
      .join('ㆍ');

    // 대운 역순 정렬은 컴포저블에서 처리
    if (sortMode.value === 'desc') {
      reverseDaeunData();
    }

    gongDataVal.value = useFun.convertGongmangTaewol(data);

    // 오행구성 수정
    birthFiveCnt.value = five.fiveColorCnt(curDaeun.value, data.birthganji?.five);

    //갤러리닫기
    rsDataNor.value = true;

    //기존 데이터 저장해 두기
    oldFormData.value = cloneDeep(formData.value);
    //oldFormData.value = formData.value;

    // 사주 조회시 갤러리형 자동으로 펼쳐짐
    rsDataNor.value = true;

    //FIXME: 전문가용 데이터 호출
    await setData();
  } catch (error) {
    console.error('Failed to load users:', error);
  } finally {
    //rsData01.value =false;
    isLoading.value = false;
  }
};
// getWoon, getSeun, getMonth는 useWoonManagement에서 가져옴

// hapBlDataSky, hapBlDataEarth, clickDaeunCheck, updateSelectedWoon, applyWoon은 useWoonManagement에서 가져옴

/* //FIXME:  순서변경 */
const sortChange = () => {
  addContentLogAPI({
    contentType: 'sl_option_sort_opt3',
    option3: sortMode.value === 'desc' ? '년월일시' : '시일월년',
  });
  sortMode.value = sortMode.value === 'desc' ? 'asc' : 'desc';
  birthTitle.value = birthTitle.value.split('').reverse().join('');
  dataOrder.value.reverse();
  reverseDaeunDataWithUpdate();
  setSortModeOption(sortMode.value);
};

// reverseDaeunData는 useWoonManagement에서 가져옴
// reverseData는 internal 유틸리티로 남겨둠
const reverseData = (data) => {
  return Object.values(data).reverse();
};

// rsDataAll 업데이트를 위한 래퍼 함수
const reverseDaeunDataWithUpdate = () => {
  reverseDaeunData();
  rsDataAll.value.daeuns.sky = daeunsData.value.sky;
  rsDataAll.value.daeuns.earth = daeunsData.value.earth;
  rsDataAll.value.wonjinsal = reverseData(rsDataAll.value.wonjinsal);
  rsDataAll.value.guimunsal = reverseData(rsDataAll.value.guimunsal);
  setData();
};

/*글자 및 색상변경*/
const searchEowayTrans = async (mode) => {
  addContentLogAPI({
    contentType: 'sl_option_chn_kor_opt3',
    option3: mode === 'CHNKOR' ? '전체' : mode === 'KOR' ? '한글' : '한자',
  });

  // formData만 먼저 설정 (langMode는 나중에)
  if (mode === 'CHNKOR') {
    formData.value.lang = 'KOR';
    oldFormData.value.lang = 'KOR';
  } else {
    formData.value.lang = mode;
    oldFormData.value.lang = mode;
  }
  formData.value.langYN = 'Y';

  // 서버에서 한자 변환된 데이터 받아오기 (로딩 스피너 없이)
  await searchEoway(true, false); // useInputData=true, showLoading=false

  // 데이터 받아온 후에 langMode 업데이트 (이제 한 번에 한자로 렌더링됨)
  langMode.value = mode;

  // langMode 변경 후 천간/지지 변화 텍스트 재계산 (convertHap은 langMode에 따라 한자/한글 변환)
  if (rsDataAll.value?.hap) {
    hapDataVal.value = five
      .convertHap({ hap: rsDataAll.value.hap, isSky: true, langMode: langMode.value })
      .join('ㆍ');
    hapDataValEarth.value = five
      .convertHap({ hap: rsDataAll.value.hap, isSky: false, langMode: langMode.value })
      .join('ㆍ');
  }

  setLangModeOption(mode);
  if (isDaeunCheck.value) applyWoon({ isLangChanged: true });
};

const searchEowayTransColor = (mode) => {
  addContentLogAPI({
    contentType: 'sl_option_color_opt3',
    option3: mode === 'fntcolor01' ? '바탕색' : mode === 'fntcolor02' ? '글자색' : '흑백',
  });
  colorMode.value = mode;
  setColorSelectOption(mode);
};

const id = ref(null);
const pgmode = ref('rs');

const isUndeletableSaju = computed(() => false);

const shareResultTextByNative = async () => {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return false;

  const shareData = {
    title: '낭만만세력 사주결과',
    text: useFun.convertShareTxt(rsDataAll.value),
  };

  if (typeof navigator.canShare === 'function' && !navigator.canShare(shareData)) {
    return false;
  }

  await navigator.share(shareData);
  return true;
};

const isShareCanceledError = (error) => {
  const errorName = error?.name || '';
  const errorMessage = error?.message || '';
  return (
    errorName === 'AbortError' ||
    /abort|cancel|dismiss/i.test(errorName) ||
    /abort|cancel|dismiss/i.test(errorMessage)
  );
};

// 모바일 공유하기
const isMobileShareOpen = ref(false);
const openMobileShare = async () => {
  addContentLogAPI('sl_btn_share');

  if (Object.keys(rsDataAll.value || {}).length < 1) {
    showDialogAlert('사주조회 후 이용하여 주시기 바랍니다.');
    return;
  }

  const isMobileDevice = typeof window !== 'undefined' && window.innerWidth <= 768;
  if (isMobileDevice) {
    try {
      const isShared = await shareResultTextByNative();
      if (isShared) return;
    } catch (error) {
      if (isShareCanceledError(error)) return;
      DEV_MODE && console.error('기기 기본 공유 실패:', error);
    }
  }

  isMobileShareOpen.value = true;
};
const closeMobileShare = () => {
  isMobileShareOpen.value = false;
};

watch(
  selectedOptions,
  (newOptions) => {
    if (newOptions) {
      const { isKoreaTime, earlyTime, birthType, langType, fiveElementTheme, wonGukSort } =
        newOptions;

      // 야자시/조자시
      formData.value.option1 = earlyTime;
      // 절기 선택
      formData.value.option2 =
        birthType === 'winter'
          ? true
          : birthType === 'spring'
            ? false
            : birthType === 'unknown'
              ? null
              : true;
      // 한국시 적용 - 값이 다를 때만 업데이트
      if (formData.value.option4 !== isKoreaTime) {
        formData.value.option4 = isKoreaTime;
      }
    }
  },
  { immediate: true },
);
const proBodyRef = ref(null);
const popQuickMenuRef = ref(null);
onMounted(async () => {
  //모바일 앱 다운로드 화면 이동 (DEV_MODE에서는 실행 안 함)
  // if (!DEV_MODE && window.innerWidth < 768) {
  //   router.push('/onboarding');
  // }

  id.value = route.params?.id;

  try {
    await getParams();
    await searchEoway();
    addContentLogAPI({
      contentType: 'sl_option_pro_gen_opt3',
      option3: '프로',
    });
  } catch (error) {
    showDialogAlert('getParams : ' + error.response?.data?.message);
  }

  isShowBanner.value = false;
  // 저장하기에서 로그인 후 재접속했을 때,
  if (!pgmode.value) {
    const userId = await getUserLogin();
    const isLoadData = route.query?.loadTempFormData === 'true';

    if (userId && manseStore.tempFormData && isLoadData) {
      formData.value = manseStore.tempFormData;
      areaTxt.value = manseStore.tempFormData.addressTxt;

      if (route.query?.redirectOption) {
        switch (route.query.redirectOption) {
          case 'ilgan':
            await searchEowayDo(() => clickIlganButton('일반'));
            break;
          case 'reading':
            await searchEowayDo(() => popQuickMenuRef.value?.navigateToAnalysis('reading'));
            break;
          case 'fortune':
            await searchEowayDo(() => popQuickMenuRef.value?.openSajuIljinDialog());
            break;
          case 'yongsin':
            await searchEowayDo(() => popQuickMenuRef.value?.navigateToAnalysis('analysis'));
            break;
          case 'personality':
            await searchEowayDo(() => popQuickMenuRef.value?.navigateToAnalysis('disposition'));
            break;

          default:
            await searchEowayDo();
            break;
        }
      } else {
        await searchEowayDo();
      }

      manseStore.clearTempFormData();
    } else if (!isLoadData) {
      manseStore.clearTempFormData();
    }
  }
});
const memoContent = ref('');
const memoCount = ref(0);
/** getDetail 응답에서 받은 isMySaju 플래그 (서버 마스킹 적용 여부의 권위 있는 신호) */
const isMySajuFromDetail = ref(false);
async function getParams() {
  DEV_MODE && console.log('getParams 호출됨');
  try {
    const result = await getDetail(id.value);
    sajuCreatedDate.value = result.createdAt;
    isMySajuFromDetail.value = result.isMySaju === true;
    const { integrationBirth } = result;

    // 내 사주는 birth가 마스킹돼 내려올 수 있으므로 파싱은 건너뜀.
    // mySaju 모드에선 fastSearchByMySaju가 guestUserId 기반으로 응답을 채워주므로
    // formData의 birth/시간 값이 비어도 화면 동작에 영향 없다.
    if (!result.isMySaju && integrationBirth && integrationBirth.includes('T')) {
      const dateTime = integrationBirth.split('T');
      const [year, month, day] = dateTime[0].split('-');
      const [hour, min] = dateTime[1]?.split(':') || [];

      formData.value.year = year;
      formData.value.month = month.padStart(2, '0');
      formData.value.day = day.padStart(2, '0');
      formData.value.hour = (hour || '').padStart(2, '0');
      formData.value.min = (min || '').padStart(2, '0');
    }

    formData.value.birthTime = result.birthTime;
    rsGuestUserId.value = result.guestUserId;
    formData.value.userName = result.guestUserName;
    // 선택된 그룹정보 저장
    selectedGroup.value = result;
    userGroupId.value = result.userGroup?.userGroupId;
    userGroupName.value = result.userGroup?.name || '등록 사주';

    memoCount.value = result.memoCount;
    //selectGender.value = result.gender === 'MEN' ? 1 : 0;
    formData.value.gender = result.gender === 'MEN' ? 1 : 0;
    formData.value.isLunar = result.isLuna === null ? false : result.isLuna;
    formData.value.isLeapYear = result.isLeapYear;
    formData.value.addressTxt = result.bornArea;
    formData.value.bornAreaId = `${result.bornAreaId}`;
    formData.value.option1 = result.option1;
    formData.value.option2 = result.option2;
    formData.value.option3 = result.option3;
    formData.value.option4 = result.option4;
    formData.value.option5 = result.option5;
    DEV_MODE && console.log('선택된그룹:', userGroupName.value);
  } catch (error) {
    goEorror01();
    console.log(error);
    // 에러 처리
    throw error; // 에러가 발생하면 다시 throw
  }
}

/** 조회된 사주 데이터 초기화 */
const resetSajuData = () => {
  sajuData.value = {};
  userGroupName.value = '등록 사주';
  userGroupId.value = null;
  memoContent.value = '';
  memoCount.value = 0;
};

// saveSajuData, convertSajuData, convert 함수들, saveSajuMemo, changeGroup은 useSajuSave에서 가져옴
const changeGroup = sajuSave.changeGroup;

function handleGroupChange(selectedGroupItem) {
  addContentLogAPI('');
  checkUserLoginAndOpenDialog().then((isLoggedIn) => {
    if (isLoggedIn) {
      groupListOpen();
      selectedGroup.value = selectedGroupItem;
      selectedListItem.value = selectedGroupItem;
      DEV_MODE && console.log('selectedGroup', selectedGroup.value);
      DEV_MODE && console.log('selectedListItem', selectedListItem.value);
    }
  });
}

//삭제
async function delComLast() {
  addContentLogAPI('');
  const param = {
    guestUsersIds: id.value,
  };

  const targetId = Number(id.value ?? route.params.id);
  const primaryId = Number(primarySajuStore.primarySajuId);
  const mineId = Number(primarySajuStore.mySajuId);
  const isMy = !!mineId && targetId === mineId;
  const isPrimary = !!primaryId && targetId === primaryId;

  let title = '사주를 삭제하시겠습니까?';
  let text = '삭제된 정보는 다시 볼 수 없습니다.';
  if (isMy && isPrimary) {
    title = '내 사주이자 홈 낭만브리핑 사주를 삭제하시겠습니까?';
    text =
      '삭제 후 부가정보에서 내 사주를 다시 등록할 수 있어요.\n메인 페이지 낭만브리핑이 비워지고, 다른 사주를 지정해야 표시돼요.';
  } else if (isMy) {
    title = '내 사주를 삭제하시겠습니까?';
    text = '삭제 후 부가정보에서 다시 등록할 수 있어요.';
  } else if (isPrimary) {
    title = '홈 낭만브리핑 사주를 삭제하시겠습니까?';
    text = '삭제하면 메인 페이지 낭만브리핑이 비워지고, 다른 사주를 지정해야 표시돼요.';
  }

  showDialogConfirm(
    title,
    text,
    '삭제',
    async () => {
      await nextTick();
      confirmDeleteSaju(param);
    },
    '※  일진 서비스 결제 · 이용내역도 함께 삭제되며 \n복구되지 않으니 주의하세요.',
    'confirm-highlight-orange',
  );
}
const confirmDeleteSaju = async (param) => {
  showDialogConfirm(
    '정말 삭제하시겠습니까?',
    '영구적으로 사주 정보가 삭제되고, \n 이용내역은 복구가 불가능합니다.',
    '삭제',
    async () => {
      await userGroupDel(param);
      router.push('/saju-service');
    },
    undefined,
    'confirm-highlight-orange',
  );
};

const areaTxt = ref('');

watch(areaTxt, (newArea) => {
  if (newArea && newArea.length > 0) {
    setUserSelectOption('isKoreaTime', true);
    formData.value.option4 = true;
  }
});

// 전문가용 일간 팝업
const isPopIlgan = ref(false);
const isIlganClosed = ref(false);
const closePopIlgan = () => {
  isPopIlgan.value = false;
  isIlganClosed.value = true;
};

const isOpenPopFiveStar = ref(false);
const openPopFiveStar = (mode) => {
  const args = {
    contentType: 'sl_btn_click_ohangdo_opt3',
    option3: mode,
  };
  addContentLogAPI(args);

  isOpenPopFiveStar.value = true;
};
const closePopFiveStar = () => (isOpenPopFiveStar.value = false);

const isPopMansePrint = ref(false);
const closePopMansePrint = () => {
  isPopMansePrint.value = false;
};

async function printManse() {
  let cnt = 0;
  cnt = Object.keys(rsDataAll.value)?.length;
  if (cnt > 0) {
    // 출력물 세션 저장
    sessionStorage.setItem('printData', JSON.stringify(rsDataAll.value));
    sessionStorage.setItem('printformData', JSON.stringify(sajuDataForm.value));
    sessionStorage.setItem('sortMode', sortMode.value);
    sessionStorage.setItem('langMode', langMode.value);
    sessionStorage.setItem('toggleProMode', toggleProMode.value);
    isPopMansePrint.value = true;
  } else {
    showDialogAlert('사주조회 후 이용하여 주시기 바랍니다.');
  }
}

const selectedMemoListItem = ref({});
const selectedMemoListItemVal = ref(null);
const memoModeType = ref(null);

//메모 열기
const openMemoRegFirst = () => {
  addContentLogAPI('');
  checkUserLoginAndOpenDialog().then((isLoggedIn) => {
    if (isLoggedIn) {
      const item = {};
      item.userName = formData.value.userName;
      item.birthTime = `${formData.value.hour}:${formData.value.min}`;
      item.gender = useFun.getGenderTxt3(formData.value.gender);
      item.solar = transdata.value.solar;
      item.lunar = transdata.value.lunar;
      item.age = transdata.value.age;
      item.ageKor = transdata.value.kor_age;
      item.firstReg = true;
      item.guestUserMemoContent = memoContent.value;
      item.guestUserId = id;
      if (memoCount.value > 0) {
        selectedMemoListItem.value = item;
        openMemoList();
      } else {
        selectedMemoListItem.value = item;
        selectedMemoListItemVal.value = item;
        memoModeType.value = 'reg';
        openMemoReg();
      }
    }
  });
};

//메모저장
const saveMemoFirst = (mContent) => {
  memoContent.value = mContent;
  memoCount.value = 1;
};

const memoData = ref([]);
const memoListData = ref({});
const listGroupMemoRefresh = async (memoTxt = '') => {
  try {
    const params = {};
    if (memoTxt !== '') params.content = memoTxt;
    // params.guestUserId = selectedMemoListItem.value.guestUserId
    params.guestUserId = route.params.id;
    params.sort = 'createdAt.desc';
    params.size = 30;
    const data = await getMemoList(params); // 그룹리스트 조회
    memoData.value = data.content;

    // listData 초기화
    Object.keys(memoListData.value).forEach((key) => {
      delete memoListData.value[key];
    });

    // 데이터 그룹화
    memoData.value.forEach((data) => {
      const createdAt = new Date(data.createdAt);
      const monthYear = `${createdAt.getFullYear()}.${`0${createdAt.getMonth() + 1}`.slice(-2)}`;

      if (!memoListData.value[monthYear]) {
        memoListData.value[monthYear] = [];
      }

      memoListData.value[monthYear].push(data);
    });
    memoCount.value = memoData.value.length;
  } catch (error) {
    console.error('Failed to load users:', error);
  }
};

const listGroupMemoDel = () => {
  showDialogAlert('삭제되었습니다.', '');
  listGroupMemoRefresh();
};

function openMemoListReg(selectedItem) {
  selectedMemoListItem.value = selectedItem;
  selectedMemoListItemVal.value = null;
  //selectedMemoListItem.value.content = ''
  memoModeType.value = 'list';
  openMemoReg();
}

function openMemoListView(selectedMemo, selectedItem) {
  selectedMemoListItemVal.value = selectedItem;
  selectedMemoListItem.value = selectedMemo;
  //memoModeType.value = 'view'
  memoModeType.value = 'edit';
  openMemoReg();
}

const dialogAlert = ref(null);
const dialogTitle = ref('');
const dialogTitleClass = ref('');
const dialogTitle2 = ref('');
const dialogText = ref('');
const dialogNextUrl = ref(null);
const warningTxt = ref('');

const dialogConfrim = ref(null);
const doFun = ref(null);

//FIX 알림창
const showDialogAlert = (title, text = '', nextUrl = '') => {
  dialogTitle.value = title;
  if (text) {
    dialogText.value = text;
  } else {
    dialogText.value = '';
  }
  dialogNextUrl.value = nextUrl;
  dialogAlert.value.openAlertDialog();
};
const showDialogConfirm = (
  title,
  title2 = '',
  text = '',
  onConfirm = () => {},
  warning,
  titleClass = '',
) => {
  dialogTitle.value = title;
  dialogTitleClass.value = titleClass;
  dialogTitle2.value = title2;
  dialogText.value = text;
  dialogNextUrl.value = onConfirm; // 함수로 전달
  warningTxt.value = warning;

  dialogConfrim.value.openConfirmDialog();
};

const isPopNameBox = ref(false);

const openUserName = () => {
  addContentLogAPI('');
  isPopNameBox.value = true;
};

const onChangeName = async (name) => {
  await updateGuestUserName(id.value, name);
  isPopNameBox.value = false;
  inputdata.value.name = name;
  await getParams();
  await searchEoway();
};

/** 언어 선택 업데이트 */
const setLangModeOption = (langMode) => {
  if (!langMode) return;

  const langTypeMap = {
    CHNKOR: 'MIX',
    CHN: 'HANJA',
    KOR: 'KOR',
  };

  const langType = langTypeMap[langMode] || 'MIX';

  setUserSelectOption('langType', langType);
};
/** 컬러 선택 업데이트 */
const setColorSelectOption = (fntcolor) => {
  if (!fntcolor) return;

  const themeMap = {
    fntcolor01: 'BACKGROUND',
    fntcolor02: 'TEXT',
    fntcolor03: 'BLACKWHITE',
  };

  const theme = themeMap[fntcolor] || 'BACKGROUND';

  setUserSelectOption('fiveElementTheme', theme);
};
/** 정렬 선택 업데이트 */
const setSortModeOption = (sortMode) => {
  if (!sortMode) return;

  const sortMap = {
    desc: 'HHDDMMYYYY',
    asc: 'YYYYMMDDHH',
  };

  const wonGukSort = sortMap[sortMode] || 'HHDDMMYYYY';

  setUserSelectOption('wonGukSort', wonGukSort);
};

/** 로그인 확인했을 때 */
const redirectToLogin = () => {};

/** 로그인 confirm dialog */
// 게스트일 경우 로그인 요청
const checkUserLoginAndOpenDialog = async () => {
  const userId = await getUserLogin();

  if (!userId) {
    showDialogConfirm('로그인 후 이용 가능합니다.', '', '로그인', () => redirectToLogin({}));
    return false;
  }
  return true; // 로그인된 상태일 경우 true 반환
};

/** 용어 설명 팝업 열기 */
const openDetail = (index) => {
  currentCardIndex.value = index;
  isPopManseDetail.value = true;
  addContentLogAPI({
    contentType: 'sl_saju_card_popup_opt3',
    option3: `${contentOptionMap[index]}`,
  });
};

const ilganInfo = ref({});
/** 오행도 내 '일간' 버튼 클릭 */
const openIlgan = (data) => {
  const args = {
    contentType: 'sl_btn_click_ilgan_opt3',
    option3: toggleProMode.value === 'pro' ? '프로' : '일반',
  };
  addContentLogAPI(args);

  // console.log('index에서',data)
  isPopIlgan.value = true;
  ilganInfo.value = data;
  // console.log('ilganInfo', ilganInfo.value)
};

/* rs 모드 일 경우 본인 정보가 아닐 경우 /error/error01 */
const goEorror01 = () => {
  router.push(isApp.value ? '/saju-service' : '/error/error01');
};

// 일반 - 오행도
const selectedData = ref(null);

const isActive = ref(false);
const handleSkyDayClick = ({ globalIdx, isSkyDayClicked }) => {
  if (selectedData.value === globalIdx) {
    // 동일한요소 재 클릭시 초기화
    resetSkyDay();
  } else {
    // 새로운 요소 선택시
    if (globalIdx === -1) {
      return;
    } else {
      selectedData.value = globalIdx;
      isActive.value = true;
    }
  }
};
const starRef = ref(null);
const resetSkyDay = () => {
  selectedData.value = null;
  isActive.value = false;
  closePopIlgan();
  watchEffect(() => {
    if (starRef.value && starRef.value.resetSelectedState) {
      starRef.value.resetSelectedState();
    } else {
      DEV_MODE && console.log('starRef 초기화 대기 중...');
    }
  });
};

// 베너 클릭시 공지사항 이동
const goToNotice = () => {
  addContentLogAPI('');
  if (DEV_MODE) {
    window.open('/nangman-center/notice/6731');
  } else {
    window.open('/nangman-center/notice/9075');
  }
};

const fiveToDayMap = {
  목: 'thu',
  화: 'tue',
  토: 'sat',
  금: 'fri',
  수: 'wed',
};
const convertFiveToDay = (five) => {
  return fiveToDayMap[five] || '';
};
/** '일간 특성' 버튼 클릭 - mode: 프로/일반 */
const clickIlganButton = (mode) => {
  const args = {
    contentType: 'sl_btn_click_ilgan_feature_opt3',
    option3: mode,
  };
  addContentLogAPI(args);

  const ilganKor = sajuData.value.birthganji.sky.day.value;
  const ilganChn = five.getHanjaTxt(ilganKor, 'sky');
  const whichDayKor = five.getColorFiveText(ilganKor);
  const whichDay = convertFiveToDay(whichDayKor);
  ilganInfo.value = {
    char: ilganChn,
    whichDay,
  };
  isIlganClosed.value = false;
  isPopIlgan.value = true;
};
</script>

<template>
  <div class="content-main bg-body-gray" :class="[colorMode, langMode]">
    <MobileHeader
      title="사주조회"
      :show-menu="!isApp"
      :show-saju-tools="!!rsData01"
      :show-back-with-saju-tools="true"
      :is-searched="!!rsData01"
      :show-save="false"
      :hide-title="true"
      :lang-mode="langMode"
      :color-mode="colorMode"
      :sort-mode="sortMode"
      @back="goList('top')"
      @searchEowayTrans="searchEowayTrans"
      @searchEowayTransColor="searchEowayTransColor"
      @sortChange="sortChange"
      @share="openMobileShare"
    />
    <section class="inner-base manse-hd-wrap">
      <ComLatest
        ref="refComLatest"
        :pr-pgmode="pgmode"
        :pr-data="rsDataAll"
        @printclick="printManse()"
      />
    </section>

    <!-- SajuResultPlaceholder는 saju-result 페이지에서는 항상 숨김 -->
    <section>
      <LoadingSpinner v-if="isLoading" />
      <transition name="fade" appear>
        <div v-if="rsData01" class="rspageSaju">
          <section class="inner-base result-10-box" style="position: relative">
            <section ref="targetDiv" class="result-01-box mb20">
              <!-- 사주결과 헤더 -->
              <SajuResultHeader
                :inputdata="inputdata"
                :transdata="transdata"
                :birthData="birthData"
                :pgmode="pgmode"
                :langMode="langMode"
                :colorMode="colorMode"
                :sortMode="sortMode"
                :toggleProMode="toggleProMode"
                :isSaved="isSaved"
                :memoCount="memoCount"
                :userGroupName="userGroupName"
                :selectedGroup="selectedGroup"
                :solarDate="solarDate"
                :solarWeekday="solarWeekday"
                :lunarDate="lunarDate"
                :useFun="useFun"
                :five="five"
                :showHanja="showHanja"
                :showIlganBadge="true"
                :isUndeletable="isUndeletableSaju"
                @openUserName="openUserName"
                @searchEowayTrans="searchEowayTrans"
                @searchEowayTransColor="searchEowayTransColor"
                @sortChange="sortChange"
                @changeProMode="changeProMode"
                @handleGroupChange="handleGroupChange"
                @openMemoRegFirst="openMemoRegFirst"
                @regSave="() => {}"
                @delComLast="delComLast"
                @goList="goList"
                @clickIlganIcon="clickIlganButton(toggleProMode === 'pro' ? '프로' : '일반')"
              />

              <!-- //TODO : 전문가 버전 체크 -->
              <div
                class="data-sec-01-box"
                style="display: flex; justify-content: center"
                v-if="toggleProMode === 'pro'"
              >
                <div class="date-pro-root-box">
                  <SajuResultPro
                    ref="proBodyRef"
                    :birthTitle="birthTitle"
                    :dataOrder="dataOrder"
                    :rsDataAll="rsDataAll"
                    :langMode="langMode"
                    :toggleProMode="toggleProMode"
                    :firstSegment="firstSegment"
                    :secondSegment="secondSegment"
                    :thirdSegment="thirdSegment"
                    :fourthSegment="fourthSegment"
                    :storedLangMode="langMode"
                    :birthEightTxt="birthEightTxt"
                    :birthData="birthData"
                    :hapDataVal="hapDataVal"
                    :hapBlDataSky="hapBlDataSky"
                    :hapDataValEarth="hapDataValEarth"
                    :isLoading="isLoading"
                    @open-ilgan="openIlgan"
                    @close-ilgan="closePopIlgan()"
                    @openPopFiveStar="openPopFiveStar"
                    @closePopFiveStar="closePopFiveStar"
                    @clickIlganButton="clickIlganButton"
                  />
                </div>
              </div>

              <!-- //TODO : 일반 버전 체크 -->
              <div class="data-sec-01-box" v-if="toggleProMode === 'nor'">
                <div class="data-0101-root-box">
                  <SajuMyeongsik
                    ref="ManseBirthMainArea"
                    :pr-birth-data="birthData"
                    :pr-birth-jijanggan="birthJijanggan"
                    :pr-lang-mode="langMode"
                    :pr-birth-title="birthTitle"
                    :pr-transdata="transdata"
                    :pr-type="'birth'"
                    :pr-cur-daeun="curDaeun"
                    :pr-cur-seun="curSeun"
                    :selected-data="selectedData"
                    :is-active="isActive"
                    :sort-mode="sortMode"
                  />
                  <!-- 모바일: 오행요약을 사주 컬럼 바로 아래에 배치 -->
                  <OhaengSummary
                    v-if="isMobile"
                    :isDaeunCheck="isDaeunCheck"
                    :birthData="birthData"
                    :birthFiveCnt="birthFiveCnt"
                    :isApplyDaeun="isapplyWoon"
                    :hapDataVal="hapDataVal"
                    :isLoading="isLoading"
                    :hapBlDataSky="hapBlDataSky"
                    :hapDataValEarth="hapDataValEarth"
                    :hapBlDataEarth="hapBlDataEarth"
                    :rsDataAll="rsDataAll"
                    :toggleProMode="toggleProMode"
                    :five="five"
                    :langMode="langMode"
                    @clickDaeunCheck="clickDaeunCheck"
                  />
                  <!-- 모바일 퀵메뉴 - 오행요약 바로 아래에 배치 -->
                  <MobileQuickMenu
                    v-if="isMobile"
                    :ilganValue="showHanja(birthData?.sky?.day?.value, 'sky') || birthData?.sky?.day?.value || ''"
                    :ilganColorClass="five.getColorFive(birthData?.sky?.day?.value)"
                    v-model:activeMenu="mobileActiveTab"
                    @menuClick="handleMobileMenuClick"
                  />

                  <!-- 모바일 탭 컨텐츠 영역 -->
                  <div v-if="isMobile" class="mobile-tab-content">
                    <!-- 대운 탭 -->
                    <div v-show="mobileActiveTab === 'daeun'" class="mobile-tab-panel">
                      <div class="mobile-inner-wrap">
                      <div class="deaun-main-box">
                        <div class="tit-box">
                          <span>대운</span>
                          <button
                            class="btn-fold"
                            :class="{ folded: !isMobileAllSubOpen }"
                            @click="toggleMobileDaeun"
                          >
                            펼침/닫기
                          </button>
                        </div>
                        <WoonContainer
                          :pr-birth-data="daeunsData"
                          :pr-lang-mode="langMode"
                          :pr-type="'daeun'"
                          :pr-cur-num="daeunCurNum"
                          @clickDaeun="getWoon"
                          @updateBlylml="updateSelectedWoon"
                        />
                        <!-- 첫 대운 변기일 정보 (모바일) -->
                        <div v-if="bigluckinfo && firstDaeunDateFormatted" class="first-daeun-info mobile">
                          ＊<span class="first-daeun-info__label">첫 대운 변기일:</span>
                          {{ firstDaeunDateFormatted }} (출생일부터 {{ bigluckinfo.elapsed_period }})
                        </div>
                      </div>

                      <!-- 세운 -->
                      <div v-if="isDaeunYear && isMobileSeWoonOpen" class="deaun-main-box">
                        <div class="tit-box">
                          <span>세운</span>
                          <button
                            class="btn-fold"
                            :class="{ folded: !isMobileSeWoonOpen }"
                            @click="toggleMobileSeWoon"
                          >
                            펼침/닫기
                          </button>
                        </div>
                        <WoonContainer
                          ref="mobileSeunContainerRef"
                          :pr-birth-data="yearsData"
                          :pr-lang-mode="langMode"
                          :pr-type="'seun'"
                          :pr-cur-num="daeunYearCurNum"
                          @clickDaeun="getWoon"
                          @updateBlylml="updateSelectedWoon"
                        />
                      </div>

                      <!-- 월운 -->
                      <div v-if="isDaeunMonth && isMobileWolWoonOpen" class="deaun-main-box">
                        <div class="tit-box">
                          <span>월운</span>
                          <button
                            class="btn-fold"
                            :class="{ folded: !isMobileWolWoonOpen }"
                            @click="toggleMobileWolWoon"
                          >
                            펼침/닫기
                          </button>
                        </div>
                        <WoonContainer
                          ref="mobileWolunContainerRef"
                          :pr-birth-data="monthsData"
                          :pr-lang-mode="langMode"
                          :pr-type="'month'"
                          :class="'month'"
                          :pr-cur-num="daeunMonthCurNum"
                          @clickDaeun="getWoon"
                          @updateBlylml="updateSelectedWoon"
                          @applyWoon="applyWoon"
                        />
                      </div>
                      </div>
                    </div>

                    <!-- 오행도 탭 -->
                    <div v-show="mobileActiveTab === 'ohaengdo'" class="mobile-tab-panel mobile-tab-panel--ohaeng">
                      <div class="mobile-inner-wrap">
                      <OhaengChart
                        :birthData="birthData"
                        :birthEightTxt="birthEightTxt"
                        @skyDay-click="handleSkyDayClick"
                        @skyDay-reset="resetSkyDay"
                        @open-ilgan="openIlgan"
                        @close-ilgan="closePopIlgan()"
                        @openPopFiveStar="openPopFiveStar"
                        @closePopFiveStar="closePopFiveStar"
                        @clickIlganButton="clickIlganButton"
                      />
                      </div>
                    </div>

                    <!-- 신살 탭 -->
                    <div v-show="mobileActiveTab === 'sinsal'" class="mobile-tab-panel mobile-tab-panel--sinsal">
                      <div class="mobile-inner-wrap">
                      <SinsalCardGrid
                        :pgmode="pgmode"
                        :pr-data-all="rsDataAll"
                        :pr-sort-mode="sortMode"
                        :pr-lang-mode="langMode"
                        :is-mobile="true"
                        @openDetail="openDetail"
                      />
                      <div class="register-date" style="padding: 16px;">사주 등록일 {{ useFun.getDateTxt(sajuCreatedDate) }}</div>
                      </div>
                    </div>
                  </div>

                  <div style="margin-bottom: 16px" />
                  <!-- PC: 오행차트 표시 -->
                  <OhaengChart
                    v-if="!isMobile"
                    ref="starRef"
                    :birthData="birthData"
                    :birthEightTxt="birthEightTxt"
                    @skyDay-click="handleSkyDayClick"
                    @skyDay-reset="resetSkyDay"
                    @open-ilgan="openIlgan"
                    @close-ilgan="closePopIlgan()"
                    @openPopFiveStar="openPopFiveStar"
                    @closePopFiveStar="closePopFiveStar"
                    @clickIlganButton="clickIlganButton"
                  />
                  <!-- PC: 오행요약을 오행차트 아래에 배치 -->
                  <OhaengSummary
                    v-if="!isMobile"
                    :isDaeunCheck="isDaeunCheck"
                    :birthData="birthData"
                    :birthFiveCnt="birthFiveCnt"
                    :isApplyDaeun="isapplyWoon"
                    :hapDataVal="hapDataVal"
                    :isLoading="isLoading"
                    :hapBlDataSky="hapBlDataSky"
                    :hapDataValEarth="hapDataValEarth"
                    :hapBlDataEarth="hapBlDataEarth"
                    :rsDataAll="rsDataAll"
                    :toggleProMode="toggleProMode"
                    :five="five"
                    :langMode="langMode"
                    @clickDaeunCheck="clickDaeunCheck"
                  />
                </div>

                <!-- Woon_Container 시작 (PC에서만 표시) -->
                <div v-if="!isMobile" class="right-luck-box">
                  <!-- 대운 시작 -->
                  <div class="deaun-main-box">
                    <div class="tit-box">
                      <span>대운</span>
                    </div>
                    <Transition>
                      <WoonContainer
                        :pr-birth-data="daeunsData"
                        :pr-lang-mode="langMode"
                        :pr-type="'daeun'"
                        :pr-cur-num="daeunCurNum"
                        @clickDaeun="getWoon"
                        @updateBlylml="updateSelectedWoon"
                      />
                    </Transition>
                    <!-- 첫 대운 변기일 정보 -->
                    <div v-if="bigluckinfo && firstDaeunDateFormatted" class="first-daeun-info">
                      ＊<span class="first-daeun-info__label">첫 대운 변기일:</span>
                      {{ firstDaeunDateFormatted }} (출생일부터 {{ bigluckinfo.elapsed_period }})
                    </div>
                  </div>
                  <!-- 대운 종료 -->

                  <!-- 세운 시작 -->
                  <div v-if="isDaeunYear" class="deaun-main-box">
                    <div class="tit-box">
                      <span>세운</span>
                      <button
                        class="btn-fold"
                        :class="{ folded: !isSeWoonOpen }"
                        @click="toggleSeWoon"
                      >
                        펼침/닫기
                      </button>
                    </div>
                    <Transition name="slide-fade">
                      <WoonContainer
                        v-show="isSeWoonOpen"
                        :pr-birth-data="yearsData"
                        :pr-lang-mode="langMode"
                        :pr-type="'seun'"
                        :pr-cur-num="daeunYearCurNum"
                        @clickDaeun="getWoon"
                        @updateBlylml="updateSelectedWoon"
                      />
                    </Transition>
                  </div>
                  <!-- 세운 종료 -->

                  <!-- 월운 시작 -->
                  <div v-if="isDaeunMonth" class="deaun-main-box">
                    <div class="tit-box">
                      <span>월운</span>
                      <button
                        class="btn-fold"
                        :class="{ folded: !isWolWoonOpen }"
                        @click="toggleWolWoon"
                      >
                        펼침/닫기
                      </button>
                    </div>
                    <Transition name="slide-fade">
                      <WoonContainer
                        v-show="isWolWoonOpen"
                        :pr-birth-data="monthsData"
                        :pr-lang-mode="langMode"
                        :pr-type="'month'"
                        :class="'month'"
                        :pr-cur-num="daeunMonthCurNum"
                        @clickDaeun="getWoon"
                        @updateBlylml="updateSelectedWoon"
                        @applyWoon="applyWoon"
                      />
                    </Transition>
                  </div>
                  <!-- 월운 종료 -->
                  <!-- Woon Container 종료 -->

                  <div class="normal-banner" @click="goToNotice()" />
                </div>
              </div>
            </section>
          </section>
        </div>
      </transition>
    </section>

    <!-- PC: 신살카드 영역 -->
    <transition name="fade" appear>
      <section v-if="rsDataNor && toggleProMode === 'nor' && !isMobile" class="rspageSaju">
        <section ref="rsGalleryBox" class="hd-inner-base bg-tab-cont manse-btm-ect-list">
          <div class="tab-content-container">
            <div class="tab-content-box">
              <SinsalCardGrid
                :pgmode="pgmode"
                :pr-data-all="rsDataAll"
                :pr-sort-mode="sortMode"
                :pr-lang-mode="langMode"
                @openDetail="openDetail"
              />
            </div>
            <div class="register-date">사주 등록일 {{ useFun.getDateTxt(sajuCreatedDate) }}</div>
          </div>
        </section>
      </section>
    </transition>
  </div>
  <section class="btn-com1 center">
    <button class="btn-line w-large bg-w" @click="goList('bottom')">목록으로</button>
  </section>

  <!-- FIX: Quick menu bar (모바일에서는 MobileQuickMenu 사용하므로 하단 바 숨김) -->
  <QuickMenu
    ref="popQuickMenuRef"
    v-if="rsData01"
    :hideBar="isMobile"
    :rsGuestUserId="rsGuestUserId"
    :pr-inputdata="inputdata"
    :pr-sajuData="sajuData"
    :pr-birth-data="birthData"
    :lang-mode="langMode"
    :five="five"
    :transdata="transdata"
    :solar="solarDate"
    :lunar="lunarDate"
    :pgmode="pgmode"
    :isIlganClosed="isIlganClosed"
    :isUnknownTime="isUnknownTime"
    :isFutureSaju="isFutureSaju"
    @clickIlganButton="clickIlganButton"
    @close="closePopQuickMenu"
    @click:outside="closePopQuickMenu"
    @regSave="() => {}"
    @redirectToLogin="redirectToLogin"
  />

  <!-- 이름 수정 -->
  <PopNameComponent
    v-if="isPopNameBox"
    :username="inputdata.name"
    @onChangeName="onChangeName"
    @close="isPopNameBox = false"
  />

  <!-- 그룹 선택 팝업 -->
  <PopSajuGroupChange
    v-if="isPopSajuGroupChange"
    :pr-selected-group="selectedGroup"
    :isDialogVisibleChange="true"
    @click="closeGroupChange"
    @change="changeGroup"
  />

  <!-- 그룹관리 팝업 -->
  <PopAddGroup
    v-if="isPopSajuGroupAdd"
    :currentTab="currentTab"
    @close="closeGroupAdd"
    @change="changeGroup"
    @refresh="groupListDataRefresh"
    @openSetting="openGroupSetting"
  />

  <!-- 메모 팝업 -->
  <PopMemo
    v-if="isPopMemoList || isPopMemoReg"
    :name="inputdata.name"
    :guest-user-id="route.params.id"
    :pr-selected-memo="selectedMemoListItem"
    :pr-selected-memo-val="selectedMemoListItemVal"
    :pr-memo-mode-type="memoModeType"
    :pr-memo-list-data="memoListData"
    :pr-transdata="transdata"
    :pr-inputdata="inputdata"
    @getMemoList="listGroupMemoRefresh"
    @openMemoView="openMemoListView"
    @openMemoReg="openMemoListReg"
    @saveMemoFirst="saveMemoFirst"
    @listGroupMemoDel="listGroupMemoDel"
    @listGroupMemo="listGroupMemoRefresh"
  />

  <!-- 출력하기 화면 -->
  <PopMansePrint
    ref="dialogPrint"
    v-if="isPopMansePrint"
    :toggleProMode="toggleProMode"
    @close="closePopMansePrint()"
    @click:outside="closePopMansePrint()"
  />

  <!-- 사주용어 이해하기 - 신살카드 팝업 -->
  <PopCardDetail
    ref="dialogDetail"
    v-if="isPopManseDetail"
    :currentCardIndex="currentCardIndex"
    @close="closePopManseDetail()"
    @click:outside="closePopManseDetail()"
  />

  <!-- 일간 특성 팝업 -->
  <PopIlgan
    v-if="isPopIlgan"
    :toggleProMode="toggleProMode"
    :pgmode="pgmode"
    :ilganInfo="ilganInfo"
    @close="closePopIlgan"
    @click:outside="closePopIlgan"
    @redirectToLogin="redirectToLogin"
  />

  <!-- 낭만만세력 오행도 팝업 -->
  <PopFiveStar
    v-if="isOpenPopFiveStar"
    @close="closePopFiveStar"
    @click:outside="closePopFiveStar"
  />

  <!-- 모바일 공유하기 팝업 -->
  <PopShareBox
    v-if="isMobileShareOpen"
    :pr-pgmode="pgmode"
    :pr-data="rsDataAll"
    @close="closeMobileShare"
    @click:outside="closeMobileShare"
  />

  <AlertComponent
    ref="dialogAlert"
    :title="dialogTitle"
    :text="dialogText"
    :nextUrl="dialogNextUrl"
    :doFun="doFun"
  />

  <ConfirmComponent
    ref="dialogConfrim"
    :title="dialogTitle"
    :titleClass="dialogTitleClass"
    :title2="dialogTitle2"
    :text="dialogText"
    :nextUrl="dialogNextUrl"
    :warning="warningTxt"
  />
</template>

<style lang="scss">
@import '@/assets/css/style.scss';
@import '@/assets/nangman_style/manse.scss';
@import '@/assets/nangman_style/manse_k.scss';
</style>

<style>
.rspage {
  height: 0;
  width: 0;
  visibility: hidden;
}

.tit-row {
  display: flex;
  flex-direction: row;
  gap: 4px;
}
@media (min-width: 600px) {
  .v-dialog.v-dialog-sm .v-overlay__content,
  .v-dialog.v-dialog-lg .v-overlay__content,
  .v-dialog.v-dialog-xl .v-overlay__content {
    inline-size: auto !important;
  }
}
</style>
<style lang="scss" scoped>
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  opacity: 0;
  position: absolute;
  background: #fff;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
}

// 첫 대운 변기일 정보 스타일
.first-daeun-info {
  margin-top: 8px;
  text-align: right;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #949494;
  padding-right: 30px; // 대운 컬럼 오른쪽 끝에 맞춤

  &__label {
    font-weight: 700;
  }

  // 모바일용 왼쪽 정렬
  &.mobile {
    text-align: left;
    padding-right: 16px;
    padding-left: 16px;
    margin-top: 0;
    margin-bottom: 16px;
    font-size: 12px;
    line-height: 18px;
  }
}

.content-main .inner-base.manse-hd-wrap {
  width: 100%;
  max-width: 1360px;
}

// 모바일 스크롤바 숨기기
@media (max-width: 768px) {
  :global(html),
  :global(body),
  :global(.manse-layout),
  :global(.manse-layout *) {
    -ms-overflow-style: none !important;
    scrollbar-width: none !important;
  }

  :global(html::-webkit-scrollbar),
  :global(body::-webkit-scrollbar),
  :global(.manse-layout::-webkit-scrollbar),
  :global(.manse-layout *::-webkit-scrollbar) {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    background: transparent !important;
  }
}

// 모바일 오행요약 스타일 (사주 컬럼처럼 흰색 배경)
:deep(.data-0101-state) {
  @media (max-width: 768px) {
    background: #fff;
    border-radius: 12px;
    margin: 16px;
  }
}

// 모바일 탭 컨텐츠 영역
.mobile-tab-content {
  background: #fff;

  // 운 컨테이너 스타일 - grid 균등 분배, 좁을 때 스크롤바 없이 가로 스크롤
  :deep(.data-0102-box) {
    display: grid !important;
    grid-template-columns: repeat(10, minmax(47.6px, 1fr)) !important;
    gap: 0 !important;
    padding: 8px 12px 12px 12px !important;
    overflow-x: auto !important;
    -ms-overflow-style: none;
    scrollbar-width: none;
    background: #fff !important;

    &::-webkit-scrollbar {
      display: none;
    }

    &.month {
      grid-template-columns: repeat(12, minmax(39.7px, 1fr)) !important;
    }

    .birth-item-box {
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;
      width: auto !important;
      padding: 8px 2px !important;
      gap: 1px !important;
      background: transparent !important;
      border-radius: 10px !important;
      cursor: pointer !important;
      position: relative !important;
      opacity: 0.7 !important;
      border: none !important;

      &.active {
        opacity: 1 !important;

        .no {
          color: #EB4C10 !important;
        }
      }

      // active 보더 스타일
      &::before {
        border-radius: 10px !important;
      }

      // 모바일에서 hover 회색 테두리 제거 (터치 시 active 주황색만 표시)
      &:hover::before {
        border-color: transparent !important;
        box-shadow: none !important;
        opacity: 0 !important;
      }
      &.active:hover::before {
        opacity: 1 !important;
        border: 1px solid #EB4C10 !important;
      }

      li {
        width: 100% !important;
        height: auto !important;
        min-height: 14px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        border-bottom: none !important;
        padding: 0 !important;

        span {
          margin-bottom: 2px !important;
        }
      }

      // 나이 (번호)
      .no {
        font-size: 14px !important;
        font-weight: 700 !important;
        color: #333 !important;
        line-height: 16px !important;
      }

      // 년도
      .no-year {
        font-size: 11px !important;
        font-weight: 400 !important;
        color: #333 !important;
        line-height: 14px !important;
      }

      // 십성 (식신, 장생, 지살 등)
      .txts {
        font-size: 13px !important;
        font-weight: 400 !important;
        color: #333 !important;
        line-height: 14px !important;
        white-space: nowrap !important;
      }

      // 오행 box - 32x32
      .txt-round {
        width: 32px !important;
        height: 32px !important;
        border-radius: 8px !important;
        display: flex !important;
        flex-direction: column !important;
        align-items: center !important;
        justify-content: center !important;
        aspect-ratio: unset !important;

        .txt-main {
          font-size: 22px !important;
          font-weight: 400 !important;
          line-height: 22px !important;
        }

        // 한자+한글 모드 - 한자 왼쪽 위, 한글 오른쪽 아래
        &.chnkor {
          .chn {
            font-size: 18px !important;
            line-height: 18px !important;
            font-weight: 400 !important;
            opacity: 1 !important;
            position: absolute !important;
            top: 2px !important;
            left: 3px !important;
            transform: none !important;
          }

          .txt-main {
            font-size: 10px !important;
            line-height: 10px !important;
            font-weight: 400 !important;
            position: absolute !important;
            bottom: 2px !important;
            right: 3px !important;
          }
        }
      }

      // 천간/지지 li 간격
      li:nth-child(4),
      li:nth-child(5) {
        padding: 1px 0 !important;
      }
    }
  }

  .mobile-tab-panel {
    padding: 0;
    overflow-x: auto;
    -ms-overflow-style: none !important;
    scrollbar-width: none !important;

    &::-webkit-scrollbar {
      display: none !important;
      width: 0 !important;
      height: 0 !important;
    }

    // 오행도 탭은 가로 스크롤 차단 + 세로 스크롤만 허용 — iOS 드래그 끌림 방지
    &--ohaeng {
      overflow: hidden;
      touch-action: pan-y;

      :deep(*) {
        touch-action: pan-y;
      }
    }

    // 신살 탭은 overflow 해제 (카드 필터 시 내부 스크롤 방지)
    &--sinsal {
      overflow: visible;
      padding-bottom: 24px;

      // 신살 서브탭 - 500px에서 한 줄에 모두 보이도록 축소
      :deep(.sinsal-sub-tabs) {
        gap: 4px;
        padding: 12px 12px;

        .sinsal-sub-tabs__item {
          padding: 5px 10px;
          font-size: 12px;
        }
      }
    }

    // 오행도 모바일 스타일 - 별 안에 한자 짤림 방지
    :deep(.pro-star-box) {
      height: 420px !important;
      overflow: visible !important;
      border: none !important;

      .div-txt {
        font-size: 16px !important;
      }

      .txt-five-box span {
        width: 24px !important;
        height: 24px !important;
        font-size: 16px !important;
      }

      .txt-five-box .txt-line-box {
        width: 70px !important;
      }

      .txt-five-box.st-05 .txt-line-box {
        width: 140px !important;
      }

      // 일간·일지 뱃지 크기 축소
      .txt-five-box .skyDay-label,
      .txt-five-box .skyDay-label-left,
      .txt-five-box .skyDay-label-right {
        font-size: 9px !important;
        height: 14px !important;
        padding: 1px 3px !important;
        line-height: 10px !important;
        border-radius: 7px !important;
      }
    }

    // 대운 탭 내부 스타일
    .deaun-main-box {
      padding: 12px 0 0 0 !important;

      &:last-child {
        margin-bottom: 0;
      }

      .tit-box {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 4px;
        padding: 0 16px !important; // 양쪽 패딩

        span {
          font-size: 16px;
          font-weight: 700;
          color: #333;
        }

        .btn-fold {
          font-size: 12px;
          color: #888;
          text-indent: -9999px;
          width: 24px;
          height: 24px;
          background: url('@/assets/images_manse/icon_up.svg') no-repeat center;
          background-size: 16px;
          transition: transform 0.2s;
          border: 1px solid #e0e0e0;
          border-radius: 50%;

          &.folded {
            transform: rotate(180deg);
          }
        }
      }
    }
  }
}

</style>
